import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GenericModulePage from '../../components/generic/GenericModulePage';
import { initializeModule } from '../../utils/configInit';
import { todoConfig } from './todoConfig';
import { createTodoValidationSchema } from './todoValidation';
import { useEntity, useUpdateEntity } from '../../hooks/useSWR';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Initialize the Todo module using generic utilities
 */
const { config: initializedConfig } = initializeModule(
  todoConfig,
  createTodoValidationSchema
);

function normalizeViewerDepartmentId(user, userRecord) {
  const raw =
    userRecord?.departmentId ??
    userRecord?.userDepartment?.id ??
    user?.departmentId ??
    user?.userDepartment?.id;
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function getUserDepartmentId(user) {
  const raw = user?.departmentId ?? user?.userDepartment?.id ?? null;
  if (raw === null || raw === undefined || raw === '') return null;
  return raw;
}

function createSameDepartmentUserFilter(viewerDepartmentId) {
  return (candidate, _index, allCandidates = []) => {
    if (!candidate || candidate.userIsActive === false) return false;
    // Until viewer department is known, show all active users (avoid empty dropdown)
    if (viewerDepartmentId == null) return true;

    const candidateDepartmentId = getUserDepartmentId(candidate);
    const isSameDepartment =
      candidateDepartmentId != null &&
      String(candidateDepartmentId) === String(viewerDepartmentId);

    if (isSameDepartment) return true;

    // If nobody shares the viewer's department, fall back to all active users
    const hasAnySameDepartment = (allCandidates || []).some((user) => {
      if (!user || user.userIsActive === false) return false;
      const deptId = getUserDepartmentId(user);
      return deptId != null && String(deptId) === String(viewerDepartmentId);
    });

    return !hasAnySameDepartment;
  };
}

function formatUserOptionLabel(user) {
  if (!user) return '';
  return (
    user.userDisplayName ||
    [user.userFirstName, user.userLastName].filter(Boolean).join(' ') ||
    user.userEmail ||
    `User ${user.id}`
  );
}

function withDepartmentScopedAssignee(config, viewerDepartmentId) {
  const fields = (config.fields || []).map((field) => {
    if (field.name !== 'assignedToUserIds') return field;
    return {
      ...field,
      labelField: 'userDisplayName',
      optionLabel: formatUserOptionLabel,
      lookupFilter: createSameDepartmentUserFilter(viewerDepartmentId),
    };
  });
  return { ...config, fields };
}

/**
 * Todo Page Component with custom toggle functionality
 */
const TodoPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [returnTo, setReturnTo] = React.useState(location.state?.returnTo || '');
  const { data: currentUserRecord } = useEntity('/users', user?.id);
  const viewerDepartmentId = React.useMemo(
    () => normalizeViewerDepartmentId(user, currentUserRecord),
    [user, currentUserRecord],
  );
  // Get the update function for todos
  const updateEntity = useUpdateEntity(initializedConfig.api.baseEndpoint);

  // Set up toggle handler
  React.useEffect(() => {
    window.todoHandlers = {
      onToggleComplete: async (todoId, currentStatus) => {
        try {
          const newStatus = !currentStatus;
          
          // Update the todo using the SWR update function
          await updateEntity(todoId, { completed: newStatus });
          
          console.log(newStatus ? 'Task marked as completed!' : 'Task marked as incomplete!');
          
          return {
            success: true,
            message: newStatus ? 'Task marked as completed!' : 'Task marked as incomplete!'
          };
        } catch (error) {
          console.error('Failed to update task status:', error);
          return {
            success: false,
            message: 'Failed to update task status'
          };
        }
      }
    };

    // Cleanup on unmount
    return () => {
      delete window.todoHandlers;
    };
  }, [updateEntity]);

  React.useEffect(() => {
    const editId = location.state?.openEditId;
    const openAdd = Boolean(location.state?.openAdd);
    const returnPath = location.state?.returnTo;
    if (returnPath) {
      setReturnTo(returnPath);
    }
    if (!editId && !openAdd) {
      if (returnPath) {
        navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
      }
      return;
    }

    if (editId) {
      window.dispatchEvent(
        new CustomEvent('generic:openEditById', {
          detail: { entityName: 'Todo', id: editId },
        }),
      );
    } else if (openAdd) {
      window.dispatchEvent(
        new CustomEvent('generic:openAddByEntity', {
          detail: { entityName: 'Todo' },
        }),
      );
    }

    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    if (!returnTo) return;

    const onFormClosed = (event) => {
      const targetEntity = event?.detail?.entityName;
      if (String(targetEntity || '').toLowerCase() !== 'todo') return;
      navigate(returnTo, { replace: true });
      setReturnTo('');
    };

    window.addEventListener('generic:formClosed', onFormClosed);
    return () => window.removeEventListener('generic:formClosed', onFormClosed);
  }, [returnTo, navigate]);

  const configForRender = React.useMemo(() => {
    let config = initializedConfig;

    if (returnTo.startsWith('/todo/calendar')) {
      config = {
        ...initializedConfig,
        ui: {
          ...(initializedConfig.ui || {}),
          hideListWhenFormOpen: true,
        },
      };
    }

    return withDepartmentScopedAssignee(config, viewerDepartmentId);
  }, [returnTo, viewerDepartmentId]);

  return (
    <GenericModulePage 
      config={configForRender}
    />
  );
};

TodoPage.displayName = 'TodoPage';

export default TodoPage;
